import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { employeeService } from '../services/service';
import { usePermisoEmpleado as usePermiso } from '../../../../auth/permisosModulos';
import type { PayRuleRow, PayRuleRowValidationErrors, SelectOption, UseFormAssignPayRule, UseFormAssignPayRuleProps } from '../types/hooks';
import type { Alert, EmployeePayRulePayload } from '../types/services';

const INDEFINITE_DATE = dayjs('3000-01-01');

const emptyRowErrors: PayRuleRowValidationErrors = {
    payRuleId: '',
    startDate: '',
    endDate: '',
};

const getRowKey = (row: PayRuleRow) => `${row.employeePayRuleId ?? 'new'}-${row.payRuleId}-${row.startDate?.format('YYYY-MM-DD') ?? ''}-${row.endDate?.format('YYYY-MM-DD') ?? ''}`;

const sortRowsByDate = (rows: PayRuleRow[]) => [...rows].sort((first, second) => (first.startDate?.valueOf() ?? Number.MAX_SAFE_INTEGER) - (second.startDate?.valueOf() ?? Number.MAX_SAFE_INTEGER));

const useFormAssignPayRule = ({ rowsSelected, option, setOption, reload }: UseFormAssignPayRuleProps): UseFormAssignPayRule => {
    const { t } = useTranslation('workers.form.assign_pay_rule');
    const { showAlert, closeAlert } = useRbSystemAlert();
    const canAssignPayRule = usePermiso('asignar-regla-pago');
    const idCounter = useRef(0);

    const [openForm, setOpenForm] = useState(false);
    const [payRuleOptions, setPayRuleOptions] = useState<SelectOption[]>([]);
    const [rows, setRows] = useState<PayRuleRow[]>([]);
    const [initialRows, setInitialRows] = useState<PayRuleRow[]>([]);
    const [searchPayRule, setSearchPayRule] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<number, PayRuleRowValidationErrors>>({});

    const selectedEmployee = rowsSelected[0] ?? null;
    const employeeCreationDate = dayjs(selectedEmployee?.created_at);
    const selectedWorkerLabel = selectedEmployee ? `${selectedEmployee.person_number} - ${selectedEmployee.fullname}` : '';

    const alert: Alert = {
        t,
        showAlert: (severity, message) => showAlert(severity, message),
        closeAlert,
    };

    const buildNewRow = (): PayRuleRow => {
        idCounter.current += 1;
        return {
            id: idCounter.current,
            employeePayRuleId: null,
            employeeId: selectedEmployee?.id ?? '',
            payRuleId: '',
            startDate: null,
            endDate: null,
        };
    };

    const normalizeRows = (targetRows: PayRuleRow[]) => {
        const sortedRows = sortRowsByDate(targetRows);
        if (sortedRows.length === 0) return sortedRows;

        return sortedRows.map((row, index) => {
            const isFirstRow = index === 0;
            const isLastRow = index === sortedRows.length - 1;

            return {
                ...row,
                startDate: isFirstRow && employeeCreationDate.isValid() && (!row.startDate?.isValid() || row.startDate.isBefore(employeeCreationDate, 'day')) ? employeeCreationDate : row.startDate,
                endDate: isLastRow ? INDEFINITE_DATE : row.endDate?.isSame(INDEFINITE_DATE, 'day') ? null : row.endDate,
            };
        });
    };

    const resetForm = () => {
        setRows([]);
        setInitialRows([]);
        setPayRuleOptions([]);
        setSearchPayRule('');
        setValidationMessage('');
        setValidationErrors({});
        setShowMessage(false);
        idCounter.current = 0;
    };

    const closeForm = () => {
        setOpenForm(false);
        setOption(null);
        resetForm();
    };

    const hasChanges = () => JSON.stringify(rows.map(getRowKey)) !== JSON.stringify(initialRows.map(getRowKey));

    const handleCancel = () => {
        if (hasChanges()) {
            setShowMessage(true);
            return;
        }

        closeForm();
    };

    const loadData = async () => {
        if (!selectedEmployee) return;

        setLoadingData(true);
        const [{ payrules }, { payRules }] = await Promise.all([employeeService.getPayrules(), employeeService.getEmployeePayRules({ employeeId: selectedEmployee.id, alert })]);

        setPayRuleOptions(payrules);

        const loadedRows = payRules.map((payRule, index) => ({
            id: index + 1,
            employeePayRuleId: payRule.employee_r_pay_rule_id,
            employeeId: String(payRule.employee_id),
            payRuleId: String(payRule.pay_rule_id),
            startDate: dayjs(payRule.start_date),
            endDate: dayjs(payRule.end_date),
        }));

        idCounter.current = loadedRows.length;
        const nextRows = normalizeRows(loadedRows.length > 0 ? loadedRows : [buildNewRow()]);

        setRows(nextRows);
        setInitialRows(nextRows);
        validateRows(nextRows);
        setLoadingData(false);
    };

    const addRow = () => {
        setRows((currentRows) => {
            const updatedRows = normalizeRows([...currentRows, buildNewRow()]);
            validateRows(updatedRows);
            return updatedRows;
        });
    };

    const removeRow = (rowId: number) => {
        setRows((currentRows) => {
            if (currentRows.length === 1) return currentRows;
            const updatedRows = normalizeRows(currentRows.filter((row) => row.id !== rowId));
            validateRows(updatedRows);
            return updatedRows;
        });
    };

    const updateRow = (rowId: number, changes: Partial<Omit<PayRuleRow, 'id' | 'employeeId'>>) => {
        setRows((currentRows) => {
            const updatedRows = currentRows.map((row) => (row.id === rowId ? { ...row, ...changes } : row));
            const sortedRows = normalizeRows(updatedRows);
            validateRows(sortedRows);
            return sortedRows;
        });
    };

    const getValidationErrors = (targetRows: PayRuleRow[]) => {
        const nextErrors: Record<number, PayRuleRowValidationErrors> = {};
        const sortedRows = sortRowsByDate(targetRows);

        targetRows.forEach((row) => {
            const rowErrors: PayRuleRowValidationErrors = { ...emptyRowErrors };

            if (!row.payRuleId) rowErrors.payRuleId = t('validation.rule_required');
            if (!row.startDate?.isValid()) {
                rowErrors.startDate = t('validation.start_date_required');
            }
            if (!row.endDate?.isValid()) {
                rowErrors.endDate = t('validation.end_date_required');
            }

            if (row.startDate?.isValid() && row.endDate?.isValid()) {
                if (!row.endDate.isAfter(row.startDate, 'day')) {
                    rowErrors.endDate = t('validation.date_range_invalid');
                }

                const sortedIndex = sortedRows.findIndex((currentRow) => currentRow.id === row.id);
                const previousRow = sortedRows[sortedIndex - 1];
                const nextRow = sortedRows[sortedIndex + 1];

                if (row.endDate.isSame(INDEFINITE_DATE, 'day') && nextRow) {
                    rowErrors.endDate = t('validation.indefinite_last');
                }

                if (previousRow?.endDate?.isValid()) {
                    const expectedStart = previousRow.endDate.add(1, 'day');
                    if (!row.startDate.isSame(expectedStart, 'day')) {
                        rowErrors.startDate = row.startDate.isBefore(expectedStart, 'day') ? t('validation.date_overlap') : t('validation.gap_error');
                    }
                }

                if (nextRow?.startDate?.isValid()) {
                    const expectedEnd = nextRow.startDate.subtract(1, 'day');
                    if (!row.endDate.isSame(expectedEnd, 'day')) {
                        rowErrors.endDate = row.endDate.isAfter(expectedEnd, 'day') ? t('validation.date_overlap') : t('validation.gap_error');
                    }
                }
            }

            if (Object.values(rowErrors).some(Boolean)) {
                nextErrors[row.id] = rowErrors;
            }
        });

        return nextErrors;
    };

    const validateRows = (targetRows: PayRuleRow[] = rows) => {
        const nextErrors = getValidationErrors(targetRows);
        setValidationErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const canSubmit = Boolean(!loading && !loadingData && rows.length > 0 && Object.keys(getValidationErrors(rows)).length === 0);

    const submit = async () => {
        if (!canAssignPayRule || !selectedEmployee) return;

        if (!validateRows()) {
            setValidationMessage(t('alerts.validation_error'));
            return;
        }

        const body: EmployeePayRulePayload[] = rows
            .slice()
            .sort((a, b) => a.startDate!.valueOf() - b.startDate!.valueOf())
            .map((row) => ({
                pay_rule_id: Number(row.payRuleId),
                start_date: row.startDate!.format('YYYY-MM-DD'),
                end_date: row.endDate!.format('YYYY-MM-DD'),
            }));

        setLoading(true);
        const success = await employeeService.setEmployeePayRules({
            employeeId: selectedEmployee.id,
            body,
            alert,
        });

        if (success) {
            showAlert('success', t('alerts.success'));
            await reload();
            closeForm();
        }

        setLoading(false);
    };

    useEffect(() => {
        if (option !== 'assign_pay_rule') return;

        if (!canAssignPayRule) {
            setOption(null);
            return;
        }

        if (rowsSelected.length !== 1) {
            setValidationMessage(t('validation.one_worker'));
            setOption(null);
            return;
        }

        setOpenForm(true);
        setOption(null);
    }, [option, rowsSelected.length, setOption, t, canAssignPayRule]);

    useEffect(() => {
        if (openForm) {
            loadData();
        }
    }, [openForm]);

    return {
        t,
        title: t('title'),
        openForm,
        rowsSelected,
        selectedWorkerLabel,
        employeeCreationDate: employeeCreationDate.isValid() ? employeeCreationDate : null,
        payRuleOptions,
        rows,
        searchPayRule,
        setSearchPayRule,
        loading,
        loadingData,
        validationMessage,
        setValidationMessage,
        showMessage,
        setShowMessage,
        validationErrors,
        canSubmit,
        hasChanges,
        closeForm,
        handleCancel,
        addRow,
        removeRow,
        updateRow,
        submit,
    };
};

export { useFormAssignPayRule };
